import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild,
  OnInit,
  OnChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  OfficeService,
  CreateOrUpdateOfficeCustomerRequest,
} from '@cai-services';
import { Country, GlobalService, Office } from '../../../core';

@Component({
  selector: 'cai-office-customers',
  templateUrl: './office-customers.component.html',
  styleUrl: './office-customers.component.scss',
  standalone: false,
})
export class OfficeCustomersComponent implements OnInit, OnChanges {
  @Input() activeOffice: Office;
  @ViewChild('saveChangedButton') saveChangedButton!: ElementRef;

  customers = [];
  selectedCustomer: any = null;
  countries: Country[];
  modifyCustomerForm: FormGroup;
  showErrors = false;
  customersLoaded = false;

  constructor(
    private readonly globalService: GlobalService,
    private readonly officeService: OfficeService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadCustomers();
    this.initializeQuoteCreateForm();
    this.initializeCountries();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.selectedCustomer = null;
    this.customers = null;
    this.customersLoaded = false;
    this.modifyCustomerForm?.reset();

    if (changes.hasOwnProperty('activeOffice')) {
      this.loadCustomers();
      this.initializeQuoteCreateForm();
    }
  }

  private async loadCustomers(): Promise<void> {
    this.customers = await this.officeService.getOfficeCustomers(
      this.activeOffice.id,
    );

    this.customers.sort((a, b) => a.name.localeCompare(b.name));

    this.customersLoaded = true;
    this.cdr.detectChanges();
  }

  private initializeQuoteCreateForm(): void {
    this.modifyCustomerForm = new FormGroup({
      customer: new FormGroup({
        id: new FormControl(),
        name: new FormControl('', [Validators.required]),
        emailSelect: new FormControl(),
        email: new FormControl(),
        addressLine1: new FormControl(),
        addressLine2: new FormControl(),
        city: new FormControl(),
        state: new FormControl(),
        zipcode: new FormControl(),
        country: new FormControl(),
      }),
    });

    this.modifyCustomerForm
      .get('customer')
      .get('emailSelect')
      .valueChanges.subscribe((value) => {
        this.selectedCustomer = this.customers?.find(
          (customer) => customer.email === value,
        );
        this.prefillForm();
      });
  }

  prefillForm() {
    if (!this.selectedCustomer) {
      return;
    }
    const customerForm = this.modifyCustomerForm.get('customer');
    customerForm.get('name').setValue(this.selectedCustomer.name);
    customerForm.get('email').setValue(this.selectedCustomer.email);
    customerForm
      .get('addressLine1')
      .setValue(this.selectedCustomer.addressLine1);
    customerForm
      .get('addressLine2')
      .setValue(this.selectedCustomer.addressLine2);
    customerForm.get('city').setValue(this.selectedCustomer.city);
    customerForm.get('state').setValue(this.selectedCustomer.state);
    customerForm.get('zipcode').setValue(this.selectedCustomer.zipcode);
    customerForm.get('country').setValue(this.selectedCustomer.country);
  }

  private async initializeCountries(): Promise<void> {
    this.countries = await this.globalService.getCountries();
    this.countries.sort((countryA, countryB) =>
      countryA.countryName.localeCompare(countryB.countryName),
    );
  }

  formHasError(controlName: string, validationTypes: string[]): boolean {
    if (!this.showErrors) {
      return false;
    }
    const control = this.modifyCustomerForm.get('customer').get(controlName);
    if (!control) {
      return false;
    }
    if (!control.value) {
      const currentErrors = control.errors || {},
        newErrors = { ...currentErrors, required: true };
      control.setErrors(newErrors);
      return true;
    }
    return validationTypes.some((validationType) =>
      control.hasError(validationType),
    );
  }

  hasCustomerChanges() {
    const customerForm = this.modifyCustomerForm.get('customer');

    return (
      !this.isEqual(
        this.selectedCustomer.name,
        customerForm.get('name')?.value,
      ) ||
      !this.isEqual(
        this.selectedCustomer.addressLine1,
        customerForm.get('addressLine1')?.value,
      ) ||
      !this.isEqual(
        this.selectedCustomer.addressLine2,
        customerForm.get('addressLine2')?.value,
      ) ||
      !this.isEqual(
        this.selectedCustomer.city,
        customerForm.get('city')?.value,
      ) ||
      !this.isEqual(
        this.selectedCustomer.state,
        customerForm.get('state')?.value,
      ) ||
      !this.isEqual(
        this.selectedCustomer.zipcode,
        customerForm.get('zipcode')?.value,
      ) ||
      !this.isEqual(
        this.selectedCustomer.country,
        customerForm.get('country')?.value,
      )
    );
  }

  normalize(value: any): any {
    return value === undefined ? '' : value;
  }

  isEqual(value1: any, value2: any): boolean {
    return this.normalize(value1) === this.normalize(value2);
  }

  private buildCreateOrUpdateOfficeCustomerRequest() {
    const createOrUpdateOfficeCustomerRequest: CreateOrUpdateOfficeCustomerRequest =
      {
        email: this.modifyCustomerForm
          .get('customer')
          .get('email')
          .value.toLowerCase(),
        name: this.modifyCustomerForm.get('customer').get('name').value,
        addressLine1: this.modifyCustomerForm
          .get('customer')
          .get('addressLine1').value,
        addressLine2: this.modifyCustomerForm
          .get('customer')
          .get('addressLine2').value,
        city: this.modifyCustomerForm.get('customer').get('city').value,
        state: this.modifyCustomerForm.get('customer').get('state').value,
        zipcode: this.modifyCustomerForm.get('customer').get('zipcode').value,
        country: this.modifyCustomerForm.get('customer').get('country').value,
      };
    return createOrUpdateOfficeCustomerRequest;
  }

  async updateCustomer() {
    this.showErrors = true;
    if (this.formHasError('name', ['required'])) {
      return;
    }
    if (this.hasCustomerChanges()) {
      const formCustomer = this.buildCreateOrUpdateOfficeCustomerRequest();
      this.saveChangedButton.nativeElement.disabled = true;

      // Create customer
      this.selectedCustomer =
        await this.officeService.createOrUpdateOfficeCustomers(
          this.activeOffice.id,
          formCustomer,
        );
      this.loadCustomers();
    }
  }
}
